import { useState, useRef, useEffect } from 'react';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Copy, ExternalLink, FileText, X } from 'lucide-react';
import { Button } from '../../_shared/components';
import { COPY_VARIABLE_FIELDS, CHANNELS, interpolateTemplate, checkCopyVarGaps, type Channel, type CopyEditorTarget, type EmailType } from './waterfallTypes';

// ── Gap row (expandable to show member names) ────────────────────────────────

function GapRow({ gap }: { gap: { variable: string; count: number; memberNames: string[] } }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="ml-5">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="flex items-center gap-1 text-xs text-lemon-700 dark:text-lemon-400 hover:text-lemon-900 dark:hover:text-lemon-300 transition-colors"
      >
        {expanded
          ? <ChevronDown className="w-3 h-3 flex-shrink-0" />
          : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
        <span><span className="font-mono">{`{{${gap.variable}}}`}</span> — empty for {gap.count} {gap.count === 1 ? 'member' : 'members'}</span>
      </button>
      {expanded && (
        <ul className="ml-4 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
          {gap.memberNames.map((name, i) => (
            <li key={i} className="text-xs text-lemon-600 dark:text-lemon-500">{name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Variable picker combobox ──────────────────────────────────────────────────

function VariablePicker({ onSelect }: { onSelect: (variable: string, target: 'subject' | 'body') => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = COPY_VARIABLE_FIELDS.filter(f =>
    `${f.object} ${f.label}`.toLowerCase().includes(query.toLowerCase())
  );

  const groups: { object: string; fields: typeof filtered }[] = [];
  for (const f of filtered) {
    const last = groups.at(-1);
    if (last?.object === f.object) last.fields.push(f);
    else groups.push({ object: f.object, fields: [f] });
  }

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <input
        className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
        placeholder="Search fields…"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {groups.length === 0 ? (
            <p className="px-3 py-2 text-xs text-nightshade-400 italic">No fields match</p>
          ) : groups.map(g => (
            <div key={g.object}>
              <p className="px-3 pt-2 pb-0.5 text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider">{g.object}</p>
              {g.fields.map(f => (
                <div key={f.variable} className="flex items-center gap-1 px-2 py-1 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 group">
                  <button
                    onMouseDown={e => { e.preventDefault(); onSelect(f.variable, 'body'); setOpen(false); setQuery(''); }}
                    className="flex-1 text-left text-xs text-nightshade-700 dark:text-nightshade-300"
                  >
                    <span className="text-nightshade-400 dark:text-nightshade-500">{f.object} · </span>{f.label}
                    <span className="ml-2 font-mono text-nightshade-400 dark:text-nightshade-500 text-xs">{f.variable}</span>
                  </button>
                  <button
                    onMouseDown={e => { e.preventDefault(); onSelect(f.variable, 'subject'); setOpen(false); setQuery(''); }}
                    title="Insert into subject"
                    className="p-1 text-nightshade-300 hover:text-blueberry-500 dark:hover:text-blueberry-400 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CopyEditor sidebar (in-flow, pushes main content) ─────────────────────────

interface CopyEditorProps extends CopyEditorTarget {
  onClose: () => void;
}

const inputCls = "w-full px-3 py-2 text-sm bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition";
const selectCls = `${inputCls} cursor-pointer`;

const EMAIL_TYPE_OPTIONS: { value: EmailType; label: string }[] = [
  { value: 'manual', label: 'Manual Email Entry' },
  { value: 'ai_generated', label: 'AI Generated Message' },
  { value: 'ai_generated_with_base', label: 'AI Generated + Manual Base' },
];

export function CopyEditor({ bucketName, statusColumn, subject, body, channel, copyStatus, emailType, sampleMember, campaign, sender, matchedMembers = [], onSave, onClose }: CopyEditorProps) {
  const [draftSubject, setDraftSubject] = useState(subject);
  const [draftBody, setDraftBody] = useState(body);
  const [draftChannel, setDraftChannel] = useState<Channel>(channel);
  const [draftEmailType, setDraftEmailType] = useState<EmailType>(emailType);
  const [copyMissingError, setCopyMissingError] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);

  const isAiGenerated = draftEmailType === 'ai_generated';

  const validateCopy = (readyCheck: boolean): boolean => {
    // AI Generated mode skips all subject/body validation
    if (isAiGenerated) {
      setCopyMissingError(null);
      return true;
    }
    if (!draftSubject.trim() && !draftBody.trim()) {
      setCopyMissingError('Subject and body are required for custom copy.');
      return false;
    }
    if (!draftSubject.trim()) {
      setCopyMissingError('Subject is required.');
      return false;
    }
    if (!draftBody.trim()) {
      setCopyMissingError('Body is required.');
      return false;
    }
    if (readyCheck) {
      const missing: string[] = [];
      if (!draftBody.includes('{{First Name}}')) missing.push('{{First Name}}');
      if (!draftBody.includes('{{Sender First Name}}')) missing.push('{{Sender First Name}}');
      if (missing.length > 0) {
        setCopyMissingError(`Body must include ${missing.join(' and ')} to mark as ready.`);
        return false;
      }
    }
    setCopyMissingError(null);
    return true;
  };

  const handleSaveDraft = () => {
    if (!validateCopy(false)) return;
    onSave(draftSubject, draftBody, 'draft', draftChannel, draftEmailType);
  };

  const handleMarkReady = () => {
    if (!validateCopy(true)) return;
    onSave(draftSubject, draftBody, 'ready', draftChannel, draftEmailType);
    handleClose();
  };

  // Slide in on mount
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Sync if target switches while sidebar stays open (keyed by bucketName+statusColumn at call site)
  useEffect(() => {
    setDraftSubject(subject);
    setDraftBody(body);
    setDraftChannel(channel);
    setDraftEmailType(emailType);
  }, [bucketName, statusColumn]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 200);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const insertVariable = (v: string, target: 'subject' | 'body') => {
    if (target === 'subject') setDraftSubject(s => s + v);
    else setDraftBody(s => s + v);
  };

  return (
    /*
     * Outer shell: clips the panel during slide animation.
     * transition-[width] pushes the sibling <main> as the panel opens.
     */
    <div className={`flex-shrink-0 h-full overflow-hidden transition-[width] duration-200 ${visible ? 'w-[460px]' : 'w-0'}`}>
      {/* Inner panel: always 460px wide so content doesn't reflow during animation */}
      <div className="w-[460px] h-full flex flex-col bg-white dark:bg-nightshade-950 border-l border-nightshade-200 dark:border-nightshade-800 shadow-xl">

        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          <span className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50">{bucketName}</span>
          <span className="text-nightshade-300 dark:text-nightshade-600">·</span>
          <span className="text-sm text-nightshade-500 dark:text-nightshade-400">{statusColumn}</span>
          {copyStatus === 'ready' && (
            <span className="ml-1 inline-flex items-center gap-1 text-xs text-matcha-600 dark:text-matcha-400 font-medium">
              <CheckCircle className="w-3.5 h-3.5" /> Ready
            </span>
          )}
          <button
            onClick={handleClose}
            className="ml-auto p-1 text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors rounded-full"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

          {/* Email type */}
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-1">Email type</label>
            <select className={selectCls} value={draftEmailType} onChange={e => setDraftEmailType(e.target.value as EmailType)}>
              {EMAIL_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Channel */}
          <div>
            <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-1">Channel</label>
            <select className={selectCls} value={draftChannel} onChange={e => setDraftChannel(e.target.value as Channel)}>
              {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* Variable picker */}
          <div className={isAiGenerated ? 'opacity-40 pointer-events-none' : ''}>
            <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mb-2">Insert variable</p>
            <VariablePicker onSelect={insertVariable} />
            <p className="text-xs text-nightshade-400 mt-1.5">
              Click → body · <Copy className="w-3 h-3 inline" /> → subject
            </p>
          </div>

          {/* Subject */}
          <div className={isAiGenerated ? 'opacity-40' : ''}>
            <label className="block text-xs font-medium text-nightshade-700 dark:text-nightshade-300 mb-1">Subject</label>
            <input
              type="text"
              value={draftSubject}
              onChange={e => setDraftSubject(e.target.value)}
              placeholder="e.g. Following up from {{Event Name}}"
              className={inputCls}
              disabled={isAiGenerated}
            />
          </div>

          {/* Body */}
          <div className={isAiGenerated ? 'opacity-40' : ''}>
            <label className="block text-xs font-medium text-nightshade-700 dark:text-nightshade-300 mb-1">
              Body <span className="font-normal text-nightshade-400">(first message only)</span>
            </label>
            <textarea
              value={draftBody}
              onChange={e => setDraftBody(e.target.value)}
              rows={14}
              placeholder={`Hi {{First Name}},\n\n...`}
              className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
              disabled={isAiGenerated}
            />
          </div>

          {/* Missing template variable warnings (live from draft text) */}
          {(() => {
            const gaps = matchedMembers.length > 0
              ? checkCopyVarGaps(draftSubject, draftBody, matchedMembers, campaign ?? null, sender ?? null)
              : [];
            if (gaps.length === 0) return null;
            return (
              <div className="rounded-lg border border-lemon-200 dark:border-lemon-800 bg-lemon-50 dark:bg-lemon-950/20 p-3 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-lemon-500 flex-shrink-0" />
                  <p className="text-xs font-semibold text-lemon-800 dark:text-lemon-300">Missing variables</p>
                </div>
                {gaps.map(gap => (
                  <GapRow key={gap.variable} gap={gap} />
                ))}
              </div>
            );
          })()}

          {/* Campaign context (shown for AI email types) */}
          {draftEmailType !== 'manual' && campaign && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider">Campaign Context</p>
                {campaign.id && (
                  <a
                    href={`https://clay.lightning.force.com/lightning/r/Campaign/${campaign.id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-600 dark:text-blueberry-400 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" /> SFDC
                  </a>
                )}
              </div>
              {campaign.invite_subject && (
                <div>
                  <p className="text-xs font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Invite Subject</p>
                  <p className="text-xs text-nightshade-700 dark:text-nightshade-300 bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-2">{campaign.invite_subject}</p>
                </div>
              )}
              {campaign.invite_body && (
                <div>
                  <p className="text-xs font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Invite Body</p>
                  <pre className="text-xs text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap font-sans leading-relaxed bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{campaign.invite_body}</pre>
                </div>
              )}
              {campaign.description && (
                <div>
                  <p className="text-xs font-medium text-nightshade-500 dark:text-nightshade-400 mb-1">Description</p>
                  <p className="text-xs text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-2 max-h-32 overflow-y-auto">{campaign.description}</p>
                </div>
              )}
              {!campaign.invite_subject && !campaign.invite_body && !campaign.description && (
                <p className="text-xs text-nightshade-400 italic">No invite copy or description on this campaign.</p>
              )}
            </div>
          )}

          {/* Live preview */}
          {sampleMember && (
            <div>
              <p className="text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-1">
                Preview — <span className="font-normal text-nightshade-400">{sampleMember.contact_name ?? sampleMember.contact_email ?? 'sample'}</span>
              </p>
              {draftSubject && (
                <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mb-2">
                  <span className="text-nightshade-400 dark:text-nightshade-500">Subject: </span>
                  {interpolateTemplate(draftSubject, sampleMember, campaign ?? null, sender ?? null)}
                </p>
              )}
              <pre className="text-xs text-nightshade-700 dark:text-nightshade-300 whitespace-pre-wrap font-sans leading-relaxed bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-3 select-none">
                {draftBody
                  ? interpolateTemplate(draftBody, sampleMember, campaign ?? null, sender ?? null)
                  : <span className="text-nightshade-300 dark:text-nightshade-600 italic">Body preview will appear here…</span>
                }
              </pre>
              {sampleMember?.notes__c && (
                <div className="mt-3 bg-nightshade-50 dark:bg-nightshade-900 border border-nightshade-100 dark:border-nightshade-800 rounded-lg px-3 py-3">
                  <p className="text-xs font-medium text-nightshade-500 dark:text-nightshade-400 mb-1 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Notes
                  </p>
                  <p className="text-xs text-nightshade-600 dark:text-nightshade-300 whitespace-pre-wrap">{sampleMember.notes__c}</p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex flex-col gap-2 px-5 py-4 border-t border-nightshade-100 dark:border-nightshade-800 flex-shrink-0">
          {copyMissingError && (
            <p className="text-xs text-pomegranate-500">{copyMissingError}</p>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={handleClose}>Cancel</Button>
            <Button variant="neutral" size="sm" onClick={handleSaveDraft}>Save draft</Button>
            <Button variant="primary" size="sm" onClick={handleMarkReady}>
              <CheckCircle className="w-3.5 h-3.5" /> Mark ready
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
